import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { request, RpcErrorCode } from "sats-connect";

const BitcoinContext = createContext({
  connected: false,
  address: null,
  provider: null,
  connect: async (kind) => {},
  disconnect: () => {},
  connecting: false,
});

async function connectUnisat() {
  if (!window.unisat) throw new Error("Install UniSat wallet");
  const accounts = await window.unisat.requestAccounts();
  const network = await window.unisat.getNetwork();
  if (network !== "livenet") throw new Error("Switch UniSat to Bitcoin mainnet");
  return { address: accounts[0], provider: "unisat" };
}

async function connectXverse() {
  const result = await request("wallet_connect", {
    addresses: ["payment"],
    message: "Connect to swap BTC on Decentralizedexchange.org",
  });
  if (result.status !== "success") {
    throw new Error(result.error?.message || "Xverse connection failed");
  }
  const payment = result.result.addresses.find(a => a.purpose === "payment");
  if (!payment?.address) throw new Error("Xverse returned no payment address");
  return { address: payment.address, provider: "xverse", addressType: payment.addressType };
}

export function BitcoinProvider({ children }) {
  const [address, setAddress] = useState(null);
  const [provider, setProvider] = useState(null);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("dex-btc-wallet") : null;
    if (stored) {
      try {
        const { address, provider } = JSON.parse(stored);
        if (address && provider) { setAddress(address); setProvider(provider); }
      } catch {}
    }
  }, []);

  const connect = useCallback(async (kind) => {
    setConnecting(true);
    try {
      let res;
      // Auto-pick if not specified
      if (!kind) kind = window.unisat ? "unisat" : "xverse";
      if (kind === "unisat") res = await connectUnisat();
      else res = await connectXverse();
      setAddress(res.address);
      setProvider(res.provider);
      localStorage.setItem("dex-btc-wallet", JSON.stringify({ address: res.address, provider: res.provider }));
    } finally { setConnecting(false); }
  }, []);

  const disconnect = useCallback(() => {
    setAddress(null); setProvider(null);
    localStorage.removeItem("dex-btc-wallet");
  }, []);

  return (
    <BitcoinContext.Provider value={{ address, provider, connected: !!address, connect, disconnect, connecting }}>
      {children}
    </BitcoinContext.Provider>
  );
}

export function useBitcoinWallet() { return useContext(BitcoinContext); }

function base64ToHex(b64) {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return [...bytes].map(b => b.toString(16).padStart(2, "0")).join("");
}

export async function signAndBroadcastBitcoin({ provider, address, transactionRequest }) {
  const val = transactionRequest?.psbt || transactionRequest?.data;
  if (!val) throw new Error("Quote missing Bitcoin PSBT/tx");
  const encoding = transactionRequest?.psbtEncoding;
  let hex, base64;
  if (encoding === "hex") { hex = val; }
  else if (encoding === "base64") { base64 = val; hex = base64ToHex(val); }
  else if (/^[0-9a-f]+$/i.test(val) && val.length % 2 === 0) { hex = val; }
  else { base64 = val; hex = base64ToHex(val); }

  if (provider === "unisat") {
    if (!window.unisat) throw new Error("UniSat not detected");
    const signedHex = await window.unisat.signPsbt(hex, { autoFinalized: true });
    return await window.unisat.pushPsbt(signedHex);
  }
  // Xverse
  const psbtBase64 = base64 || Buffer.from(hex, "hex").toString("base64");
  const result = await request("signPsbt", {
    psbt: psbtBase64,
    signInputs: { [address]: [] },
    broadcast: true,
  });
  if (result.status !== "success") {
    if (result.error?.code === RpcErrorCode.USER_REJECTION) throw new Error("User rejected signing");
    throw new Error(result.error?.message || "Xverse signing failed");
  }
  if (!result.result?.txid) throw new Error("Xverse did not return a txid");
  return result.result.txid;
}
